import type { UserWorkItem } from '@/api/home/type'
import { getDreamaDomain } from '@/constants/domain'
import type { Theme } from '@/stores/theme'

export type SummaryContentObject = 'media' | 'pipe' | 'radio'
export type SummaryContentFunc = 'video' | 'mv' | 'imgtxt' | 'none' | 'ugc' | 'room' | 'radio'
export type SummaryContentDisplayKind =
  | 'media-video'
  | 'media-imgtxt'
  | 'pipe-none'
  | 'pipe-room'
  | 'radio'
  | 'unknown'

const EMPTY_FROM_ID = '000000000000000000000000'

export function resolveSummaryContentFunc(obj: string, func: string | null | undefined): SummaryContentFunc {
  if (obj === 'media') {
    return func === 'mv' || func === 'imgtxt' || func === 'video' ? func : 'video'
  }

  if (obj === 'pipe') {
    return func === 'ugc' || func === 'room' || func === 'none' ? func : 'none'
  }

  if (obj === 'radio') {
    return 'radio'
  }

  return 'video'
}

export function resolveSummaryContentKind(item: Pick<UserWorkItem, 'obj' | 'func'>): SummaryContentDisplayKind {
  const resolvedFunc = resolveSummaryContentFunc(item.obj, item.func)

  if (item.obj === 'media') {
    return resolvedFunc === 'imgtxt' ? 'media-imgtxt' : 'media-video'
  }

  if (item.obj === 'pipe') {
    return resolvedFunc === 'room' ? 'pipe-room' : 'pipe-none'
  }

  if (item.obj === 'radio') {
    return 'radio'
  }

  return 'unknown'
}

export function isUgcPipe(item: Pick<UserWorkItem, 'obj' | 'func' | 'fromId'>) {
  return item.obj === 'pipe' && (item.func === 'ugc' || item.fromId !== EMPTY_FROM_ID)
}

export function getSummaryContentAspectRatio(item: Pick<UserWorkItem, 'obj' | 'func'>) {
  const kind = resolveSummaryContentKind(item)

  if (kind === 'radio') {
    return '1 / 1'
  }

  if (kind === 'media-imgtxt') {
    return '220 / 293.3333'
  }

  return '293 / 468.8'
}

export function getSummaryContentTypeLabel(item: Pick<UserWorkItem, 'obj' | 'func'>) {
  const resolvedFunc = resolveSummaryContentFunc(item.obj, item.func)

  if (item.obj === 'radio') {
    return '听听'
  }

  if (item.obj === 'media') {
    return resolvedFunc === 'imgtxt' ? '图文' : '视频'
  }

  if (item.obj === 'pipe') {
    return resolvedFunc === 'room' ? '派对' : '互动'
  }

  return '内容'
}

export function getSummaryContentRadioTag(item: Pick<UserWorkItem, 'showTag'>) {
  return item.showTag?.types?.[0]?.tag ?? ''
}

export function buildSummaryContentViewUrl(item: UserWorkItem, theme: Theme = 'dark') {
  const domain = getDreamaDomain()
  const targetId = item.anchor?.id || item.id
  const anchorJump = item.anchor?.jump || item.jump
  const resolvedFunc = resolveSummaryContentFunc(item.obj, item.func)

  if (item.obj === 'media' || anchorJump === 'cartoon_box' || anchorJump === 'media_collection') {
    const url = new URL('/rolematerial.html', domain)
    url.searchParams.set('cartoonBoxId', targetId)
    url.searchParams.set('func', resolvedFunc === 'imgtxt' ? 'imgtxt' : resolvedFunc === 'mv' ? 'mv' : 'video')
    url.searchParams.set('theme', theme)
    url.searchParams.set('play', '1')
    return url.toString()
  }

  if (item.obj === 'radio' || anchorJump === 'radio') {
    const url = new URL('/radio.html', domain)
    url.searchParams.set('radio_id', targetId)
    url.searchParams.set('theme', theme)
    return url.toString()
  }

  if (item.obj === 'pipe' || anchorJump === 'pipe') {
    const url = new URL('/view.html', domain)
    url.searchParams.set('pipe_id', targetId)
    url.searchParams.set('func', resolvedFunc)
    url.searchParams.set('theme', 'dark')
    return url.toString()
  }

  return null
}
